<template>
  <div class="data-panel">
    <h3 class="data-title">Consumo de IA</h3>
    <p v-if="iaUsage?.pricing" class="ia-pricing-note">
      Preços: {{ iaUsage.pricing.model }} · ${{ iaUsage.pricing.inputPerM.toFixed(2) }}/1M tokens entrada · ${{ iaUsage.pricing.outputPerM.toFixed(2) }}/1M tokens saída
    </p>

    <div v-if="iaUsageLoading" style="color:#6b7280;font-size:14px;margin:16px 0;">Carregando...</div>
    <div v-else-if="!iaUsage" style="color:#9ca3af;font-size:14px;margin:16px 0;">Nenhum dado de IA disponível.</div>
    <template v-else>
      <!-- Summary cards -->
      <div class="ia-stats-grid">
        <div class="ia-stat-card ia-stat-card--cost">
          <span class="ia-stat-value">{{ formatCost(iaUsage.estimatedCostUsd) }}</span>
          <span class="ia-stat-label">Custo total estimado</span>
        </div>
        <div class="ia-stat-card ia-stat-card--cost">
          <span class="ia-stat-value">{{ formatCost(iaUsage.last30CostUsd) }}</span>
          <span class="ia-stat-label">Custo últimos 30 dias</span>
        </div>
        <div class="ia-stat-card">
          <span class="ia-stat-value">{{ iaUsage.totalRequests }}</span>
          <span class="ia-stat-label">Requisições totais</span>
        </div>
        <div class="ia-stat-card">
          <span class="ia-stat-value">{{ formatTokens(iaUsage.totalInputTokens) }}</span>
          <span class="ia-stat-label">Tokens de entrada</span>
        </div>
        <div class="ia-stat-card">
          <span class="ia-stat-value">{{ formatTokens(iaUsage.totalOutputTokens) }}</span>
          <span class="ia-stat-label">Tokens de saída</span>
        </div>
        <div class="ia-stat-card">
          <span class="ia-stat-value">{{ formatCost(avgCostPerRequest) }}</span>
          <span class="ia-stat-label">Custo médio por requisição</span>
        </div>
      </div>

      <!-- Por recurso: de onde vem o custo -->
      <h4 class="ia-section-title" style="margin-top:24px;">Por recurso de IA</h4>
      <div v-if="!iaUsage.byResource || iaUsage.byResource.length === 0" style="color:#9ca3af;font-size:14px;">Sem dados.</div>
      <div v-else class="ia-resource-list">
        <div v-for="r in iaUsage.byResource" :key="r.endpoint" class="ia-resource-row">
          <div class="ia-resource-head">
            <span class="ia-resource-label">{{ r.label }}</span>
            <span class="ia-resource-cost">{{ formatCost(r.costUsd) }} <span class="ia-resource-pct">({{ r.costSharePct }}%)</span></span>
          </div>
          <div class="ia-resource-bar-wrap">
            <div class="ia-resource-bar" :style="{ width: Math.max(2, r.costSharePct) + '%' }" />
          </div>
          <div class="ia-resource-meta">
            {{ r.requests }} req · {{ formatTokens(r.inputTokens) }} entrada · {{ formatTokens(r.outputTokens) }} saída
            <span v-if="r.lastUsedAt"> · último uso {{ formatDate(r.lastUsedAt) }}</span>
          </div>
        </div>
      </div>

      <!-- By day table -->
      <h4 class="ia-section-title" style="margin-top:24px;">Por dia (últimos 30 dias)</h4>
      <div v-if="iaUsage.byDay.length === 0" style="color:#9ca3af;font-size:14px;">Sem dados.</div>
      <div v-else class="log-scroll">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Requisições</th>
              <th>Tokens entrada</th>
              <th>Tokens saída</th>
              <th>Custo est. (USD)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="day in iaUsage.byDay" :key="day.date">
              <td>{{ formatDate(day.date) }}</td>
              <td>{{ day.requests }}</td>
              <td>{{ formatTokens(day.inputTokens) }}</td>
              <td>{{ formatTokens(day.outputTokens) }}</td>
              <td>{{ formatCost(day.costUsd) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Recent requests -->
      <h4 class="ia-section-title" style="margin-top:24px;">Últimas requisições</h4>
      <div v-if="iaUsage.recentLogs.length === 0" style="color:#9ca3af;font-size:14px;">Sem requisições.</div>
      <div v-else class="log-scroll">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Data/Hora</th>
              <th>Recurso</th>
              <th>Tokens entrada</th>
              <th>Tokens saída</th>
              <th>Custo est.</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in iaUsage.recentLogs" :key="log.id">
              <td>{{ formatDateTime(log.createdAt) }}</td>
              <td :title="log.endpoint">{{ log.label || log.endpoint }}</td>
              <td>{{ formatTokens(log.inputTokens) }}</td>
              <td>{{ formatTokens(log.outputTokens) }}</td>
              <td>{{ formatCost(log.costUsd ?? fallbackCost(log)) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, ref, inject, onMounted } from 'vue'
import { masterFetch } from '@/store/masterSession'

interface IaUsageDay {
  date: string
  requests: number
  inputTokens: number
  outputTokens: number
  costUsd: number
}

interface IaUsageResource {
  endpoint: string
  label: string
  requests: number
  inputTokens: number
  outputTokens: number
  costUsd: number
  costSharePct: number
  lastUsedAt: string
}

interface IaUsageLog {
  id: string
  endpoint: string
  label?: string
  model: string
  inputTokens: number
  outputTokens: number
  costUsd?: number
  createdAt: string
}

interface IaUsage {
  pricing?: { model: string; inputPerM: number; outputPerM: number }
  totalRequests: number
  totalInputTokens: number
  totalOutputTokens: number
  estimatedCostUsd: number
  last30CostUsd: number
  last30Requests: number
  byResource: IaUsageResource[]
  byDay: IaUsageDay[]
  recentLogs: IaUsageLog[]
}

export default {
  name: 'AdminSysIATab',
  setup() {
    const detail = inject<any>('adminDetail')

    const iaUsage = ref<IaUsage | null>(null)
    const iaUsageLoading = ref(false)

    const loadIaUsage = async () => {
      if (!detail?.value) return
      iaUsageLoading.value = true
      try {
        iaUsage.value = await masterFetch<IaUsage>(`/master/systems/${detail.value.systemId}/ia-usage`)
      } catch { /* ignore */ } finally {
        iaUsageLoading.value = false
      }
    }

    onMounted(loadIaUsage)

    const avgCostPerRequest = computed(() => {
      const u = iaUsage.value
      if (!u || !u.totalRequests) return 0
      return u.estimatedCostUsd / u.totalRequests
    })

    // Fallback p/ logs antigos sem costUsd calculado no backend
    const fallbackCost = (log: IaUsageLog) => {
      const p = iaUsage.value?.pricing
      if (!p) return 0
      return (log.inputTokens / 1_000_000) * p.inputPerM + (log.outputTokens / 1_000_000) * p.outputPerM
    }

    const formatDate = (d: string) => {
      if (!d) return ''
      try { return new Date(d).toLocaleDateString('pt-BR') } catch { return d }
    }

    const formatDateTime = (d: string) => {
      if (!d) return ''
      try { return new Date(d).toLocaleString('pt-BR') } catch { return d }
    }

    const formatCost = (usd: number) => {
      if (!usd) return '$0.00'
      if (usd < 0.01) return `$${usd.toFixed(5)}`
      return `$${usd.toFixed(2)}`
    }

    const formatTokens = (n: number) => {
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M'
      if (n >= 1000) return (n / 1000).toFixed(1) + 'k'
      return String(n ?? 0)
    }

    return { iaUsage, iaUsageLoading, avgCostPerRequest, fallbackCost, formatDate, formatDateTime, formatCost, formatTokens }
  }
}
</script>

<style lang="scss">
.ia-pricing-note {
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 20px;
}

.ia-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 8px;
}

.ia-stat-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &--cost {
    border-color: #a78bfa;
    background: #f5f3ff;
  }
}

.ia-stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}

.ia-stat-label {
  font-size: 12px;
  color: #6b7280;
}

.ia-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 10px;
}

/* ── Por recurso ── */
.ia-resource-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 640px;
}

.ia-resource-row {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ia-resource-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ia-resource-label { font-size: 13px; font-weight: 700; color: #111827; }
.ia-resource-cost { font-size: 13px; font-weight: 700; color: #7c3aed; white-space: nowrap; }
.ia-resource-pct { font-weight: 600; color: #9ca3af; font-size: 12px; }

.ia-resource-bar-wrap {
  height: 7px;
  background: #e5e7eb;
  border-radius: 99px;
  overflow: hidden;
}
.ia-resource-bar {
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, #a78bfa, #7c3aed);
  transition: width .3s;
}

.ia-resource-meta { font-size: 11.5px; color: #6b7280; }
</style>
